import { filterByHiddenItemsId, getSidebarItems } from '../actions/sidebarActions';

export const isPathActive = (pathname, path) => {
    if (!path) return false;
    if (path === '/') return pathname === '/';
    if (path === '/Ordenes') {
        return pathname === '/Ordenes' || pathname.startsWith('/Ordenes/');
    }
    return pathname === path || pathname.startsWith(`${path}/`);
};

const SELLER_TAB_PATHS = ['/', '/Publicaciones', '/Ordenes'];
const ADMIN_TAB_PATHS = ['/', '/Publicaciones', '/Ordenes', '/Sellers'];

const PARENT_DEFAULT_PATH = {
    10: '/Sellers',
    20: '/Usuarios',
    30: '/Publicaciones',
    40: '/Ordenes',
    50: '/Rebates',
    70: '/Tipo/Publicaciones',
    80: '/Exportables/Publicaciones',
    90: '/Liquidaciones',
};

const shortLabel = (label = '') =>
    label
        .replace(/^Gestión de\s+/i, '')
        .replace(/^Adm\.\s+/i, '')
        .replace(/\s+Sellers$/i, '')
        .trim() || label;

const toNavItem = (item, overrides = {}) => ({
    id: item.id,
    label: shortLabel(item.label),
    path: item.path || item.goToPath,
    section: item.section || null,
    ...overrides,
});

/** Expande hijos visibles para el sheet "M├ís"; los tabs usan un path preferido por padre. */
const flattenSidebarItems = (items = [], { expandChildren = false } = {}) => {
    const result = [];

    items.forEach((item) => {
        if (item.goToPath) {
            result.push({
                id: item.id,
                label: item.label,
                path: item.goToPath,
                section: null,
            });
            return;
        }

        const children = (item.childItems || []).filter((child) => child.goToPath);
        if (expandChildren && children.length > 0) {
            children.forEach((child) => {
                result.push({
                    id: child.id,
                    label: child.label,
                    path: child.goToPath,
                    section: shortLabel(item.label),
                });
            });
            return;
        }

        const preferredPath = PARENT_DEFAULT_PATH[item.id];
        const preferredChild = children.find((child) => child.goToPath === preferredPath);
        const firstChild = children[0];
        const target = preferredChild || firstChild;

        if (target) {
            result.push({
                id: item.id,
                label: item.label,
                path: target.goToPath,
                section: null,
            });
        }
    });

    return result;
};

const withHome = (items) => {
    if (items.some((item) => item.path === '/')) return items;
    return [{ id: 'home', label: 'Inicio', path: '/', section: null }, ...items];
};

const pickTabs = (flatItems, tabPaths) => {
    const tabs = [];
    const usedIds = new Set();

    tabPaths.forEach((path) => {
        const match = flatItems.find((item) => {
            if (item.path === path) return true;
            if (path === '/') return false;
            return item.path === path || item.path.startsWith(`${path}/`) || item.path.startsWith(`${path}?`);
        });
        if (match && !usedIds.has(match.id)) {
            tabs.push(toNavItem(match));
            usedIds.add(match.id);
        }
    });

    if (tabs.length === 0) {
        flatItems.slice(0, 4).forEach((item) => {
            tabs.push(toNavItem(item));
            usedIds.add(item.id);
        });
    }

    return { tabs, usedIds };
};

const buildNavFromSidebar = (userType, { idSeller, idUser, hiddenItemsId }, tabPaths, roleMeta) => {
    const sidebarItems = hiddenItemsId?.length
        ? filterByHiddenItemsId(getSidebarItems(userType, idSeller, idUser), hiddenItemsId)
        : getSidebarItems(userType, idSeller, idUser);
    const tabSource = withHome(flattenSidebarItems(sidebarItems, { expandChildren: false }));
    const { tabs } = pickTabs(tabSource, tabPaths);

    const moreSource = flattenSidebarItems(sidebarItems, { expandChildren: true });
    const moreItems = moreSource
        .filter((item) => item.path && item.path !== '/')
        .map((item) => toNavItem(item, { label: item.label }));

    return {
        ...roleMeta,
        tabs,
        moreItems,
    };
};

export const getMobileNav = (userType = {}, { idSeller, idUser, hiddenItemsId } = {}) => {
    const isSeller = !!(userType.isSeller || userType.isSellerAdmin);

    if (isSeller) {
        return buildNavFromSidebar(
            userType,
            { idSeller, idUser, hiddenItemsId },
            SELLER_TAB_PATHS,
            { role: 'seller', roleLabel: 'Seller' }
        );
    }

    const isAdmin = !!(userType.isAdminMod || userType.isCategory);
    return buildNavFromSidebar(
        userType,
            { idSeller, idUser, hiddenItemsId },
            ADMIN_TAB_PATHS,
        {
            role: isAdmin ? 'admin' : 'user',
            roleLabel: isAdmin ? 'Admin' : 'Usuario',
        }
    );
};

/** Agrupa items del sheet M├ís por secci├│n (para sellers / men├║s ricos). */
export const groupMoreItems = (moreItems = []) => {
    const groups = [];
    const indexBySection = new Map();

    moreItems.forEach((item) => {
        const section = item.section || 'Más opciones';
        if (!indexBySection.has(section)) {
            indexBySection.set(section, groups.length);
            groups.push({ section, items: [] });
        }
        groups[indexBySection.get(section)].items.push(item);
    });

    const moreOptionsIndex = groups.findIndex((group) => group.section === 'Más opciones');
    if (moreOptionsIndex !== -1 && moreOptionsIndex !== groups.length - 1) {
        const [moreOptions] = groups.splice(moreOptionsIndex, 1);
        groups.push(moreOptions);
    }

    return groups;
};
